/**
 * apps/admin UI primitives.
 *
 * COLOUR, RADIUS, SHADOW and FONT FAMILY come exclusively from the generated
 * token preset (`@navoiy/tokens/tailwind-preset`), so the panel reads as the same
 * government product as the kiosk. There is not one ad-hoc hex here.
 *
 * TYPE SCALE and SPACING deliberately use Tailwind's own scale rather than the
 * `text-body` / `px24` tokens: those tokens are dimensioned for a 32" portrait
 * kiosk at 1080×1920 (their smallest step is 28px, and `h-btn` is 128px), which
 * is unusable density for an eight-column inbox on a staff desktop. The preset
 * only `extend`s the theme, so Tailwind's default scale is still part of the
 * theme — this is a token choice, not an ad-hoc value.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type ButtonTone = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_TONE: Record<ButtonTone, string> = {
  primary: 'bg-brand-700 text-surface-0 hover:bg-brand-800 disabled:bg-ink-400',
  secondary: 'bg-surface-0 text-brand-800 border-1 border-line-300 hover:bg-surface-100',
  ghost: 'bg-transparent text-brand-700 hover:bg-surface-100',
  danger: 'bg-danger-500 text-surface-0 hover:bg-danger-600 disabled:bg-ink-400',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
}

export function Button({ tone = 'primary', className = '', ...rest }: ButtonProps): React.JSX.Element {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-btn px-4 py-2 text-sm font-semibold transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:opacity-60 ${BUTTON_TONE[tone]} ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

const CONTROL =
  'w-full rounded-chip border-1 border-line-300 bg-surface-0 px-3 py-2 text-sm text-ink-900 outline-none transition-colors duration-fast focus:border-brand-500 disabled:bg-surface-100 disabled:text-ink-500';

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-label text-ink-500">{label}</span>
      {children}
      {hint ? <span className="text-xs text-ink-400">{hint}</span> : null}
      {error ? <span className="text-xs font-medium text-danger-500">{error}</span> : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  const { className = '', ...rest } = props;
  return <input {...rest} className={`${CONTROL} ${className}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): React.JSX.Element {
  const { className = '', ...rest } = props;
  return <textarea {...rest} className={`${CONTROL} min-h-24 resize-y ${className}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  const { className = '', ...rest } = props;
  return <select {...rest} className={`${CONTROL} ${className}`} />;
}

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }): React.JSX.Element {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
      <input
        {...rest}
        type="checkbox"
        className="h-4 w-4 rounded-sm border-1 border-line-400 text-brand-600 accent-brand-600"
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  title,
  actions,
  children,
  className = '',
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <section
      className={`rounded-tile border-1 border-line-200 bg-surface-0 shadow-elev-1 ${className}`}
    >
      {title || actions ? (
        <header className="flex items-center justify-between gap-4 border-b-1 border-line-200 px-5 py-3">
          {title ? <h2 className="text-base font-semibold text-brand-900">{title}</h2> : <span />}
          {actions}
        </header>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}

type BadgeTone = 'neutral' | 'brand' | 'success' | 'danger' | 'gold';

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-surface-100 text-ink-700',
  brand: 'bg-brand-50 text-brand-800',
  success: 'bg-success-50 text-success-700',
  danger: 'bg-danger-50 text-danger-600',
  gold: 'bg-gold-100 text-gold-700',
};

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: BadgeTone;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-chip px-2 py-0.5 text-xs font-semibold ${BADGE_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

export function ErrorBanner({ message }: { message: string | null }): React.JSX.Element | null {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-chip border-1 border-danger-100 bg-danger-50 px-4 py-3 text-sm font-medium text-danger-600"
    >
      {message}
    </div>
  );
}

export function WarningBanner({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="rounded-chip border-1 border-gold-300 bg-gold-100 px-4 py-3 text-sm text-gold-900">
      {children}
    </div>
  );
}

export function Spinner({ label }: { label: string }): React.JSX.Element {
  return <p className="py-8 text-center text-sm text-ink-500">{label}</p>;
}

export function EmptyState({ message }: { message: string }): React.JSX.Element {
  return (
    <p className="rounded-chip bg-surface-50 py-10 text-center text-sm text-ink-500">{message}</p>
  );
}

// ---------------------------------------------------------------------------
// Modal — used for every create/edit form and for the destructive confirmations
// that D-032 §2.3 requires to be explicit.
// ---------------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="fixed inset-0 z-overlay flex items-start justify-center overflow-y-auto bg-scrim p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`my-8 w-full rounded-card bg-surface-0 shadow-elev-3 ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
      >
        <header className="flex items-center justify-between border-b-1 border-line-200 px-5 py-4">
          <h2 className="text-base font-semibold text-brand-900">{title}</h2>
          <Button tone="ghost" onClick={onClose} aria-label="close">
            ✕
          </Button>
        </header>
        <div className="flex flex-col gap-4 px-5 py-5">{children}</div>
        {footer ? (
          <footer className="flex justify-end gap-2 border-t-1 border-line-200 px-5 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function Table({ head, children }: { head: ReactNode; children: ReactNode }): React.JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="border-b-1 border-line-200 text-xs uppercase tracking-label text-ink-500">
          {head}
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Th({ children }: { children: ReactNode }): React.JSX.Element {
  return <th className="whitespace-nowrap px-3 py-2 font-semibold">{children}</th>;
}

export function Td({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return <td className={`px-3 py-3 align-middle text-ink-700 ${className}`}>{children}</td>;
}

export function Tr({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}): React.JSX.Element {
  return (
    <tr
      onClick={onClick}
      className={`border-b-1 border-line-100 ${onClick ? 'cursor-pointer hover:bg-surface-50' : ''}`}
    >
      {children}
    </tr>
  );
}

/** A definition row for the detail screen. */
export function DataRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(9rem,14rem)_1fr] gap-3 border-b-1 border-line-100 py-2 last:border-b-0">
      <dt className="text-xs font-semibold uppercase tracking-label text-ink-500">{label}</dt>
      <dd className="text-sm text-ink-900">{children}</dd>
    </div>
  );
}
